import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDossiers, getAuditAlerts, resolveAlert, validateDossier, deleteStartup, getStartupInvestors, getAdminNda, validateNda, rejectNda, getAdminPendingNdas, downloadNdaFile, getCandidatesCompleteness, unlockCandidature, exportCandidatesCsv } from '../../api';
import { useAuth } from '../../App';
import { useToast } from '../../components/Toast';
import { REFERRAL_SOURCES, parseReferralValue, migrateLegacyReferral } from '../../utils/referralSources';

const STATUS_LABELS = { brouillon: 'Brouillon', en_attente: 'En attente', actif: 'Actif', suspendu: 'Suspendu', archive: 'Archivé' };
const STATUS_COLORS = { brouillon: '#95A5A6', en_attente: '#F39C12', actif: '#27AE60', suspendu: '#E74C3C', archive: '#8E44AD' };
const DOSSIER_ACTION_LABELS = {
  nouveau: { label: 'Nouveau', color: '#2196F3', bg: '#E3F2FD' },
  en_cours: { label: 'En cours', color: '#FF9800', bg: '#FFF3E0' },
  valide: { label: 'Validé', color: '#4CAF50', bg: '#E8F5E9' },
  en_attente: { label: 'En attente', color: '#9C27B0', bg: '#F3E5F5' },
  rejete: { label: 'Rejeté', color: '#F44336', bg: '#FFEBEE' },
  publie: { label: 'Publié', color: '#00897B', bg: '#E0F2F1' },
};
const SEV_COLORS = { information: '#2E75B6', avertissement: '#F39C12', critique: '#E74C3C' };
const SECTEURS = ['Fintech', 'Insurtech', 'Regtech', 'Biotech', 'Greentech', 'Medtech', 'SaaS', 'HealthTech', 'Immotech'];

export default function AnalystDossiers() {
  const [dossiers, setDossiers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [tab, setTab] = useState('dossiers');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [secteurFilter, setSecteurFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [trophyFilter, setTrophyFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [submittedFilter, setSubmittedFilter] = useState('');
  const [qitMinFilter, setQitMinFilter] = useState('');
  const [exportingList, setExportingList] = useState(false);
  const [modal, setModal] = useState(null); // { type: 'deactivate'|'delete'|'nda', startup, investors }
  const [ndaModal, setNdaModal] = useState(null); // { nda, events, startup }
  const [pendingNdas, setPendingNdas] = useState([]);
  const [completeness, setCompleteness] = useState(null); // { total_candidates, qit, documents }
  const [rejectReason, setRejectReason] = useState('');
  const [ndaAction, setNdaAction] = useState(false);
  // Inline PDF preview of the uploaded NDA — blob URL revoked on unmount/close
  const [ndaPreviewUrl, setNdaPreviewUrl] = useState(null);
  const [ndaPreviewLoading, setNdaPreviewLoading] = useState(false);
  const [ndaPreviewError, setNdaPreviewError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'admin';

  const [unlockingId, setUnlockingId] = useState(null);

  const load = () => {
    getDossiers(statusFilter || undefined).then(setDossiers).catch(console.error);
    getAuditAlerts().then(setAlerts).catch(console.error);
    getCandidatesCompleteness().then(setCompleteness).catch(() => {});
    if (isAdmin) getAdminPendingNdas().then(setPendingNdas).catch(() => {});
  };

  const handleUnlockCandidature = async (e, d) => {
    e.stopPropagation();
    if (!window.confirm(
      `Déverrouiller la candidature de « ${d.name} » ?\n\n` +
      `Le verrou sur le questionnaire ISI et la Data Room sera levé : la startup ` +
      `pourra à nouveau modifier son dossier et devra le resoumettre. ` +
      `Elle en sera informée dans son journal d'événements.`
    )) return;
    setUnlockingId(d.id);
    try {
      await unlockCandidature(d.id);
      toast.success(`Candidature de « ${d.name} » déverrouillée`);
      load();
    } catch (err) {
      toast.error(err.message || 'Échec du déverrouillage');
    } finally {
      setUnlockingId(null);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  // Fetch the uploaded NDA file as a blob and expose a temporary URL for the
  // inline <iframe> viewer. The URL is revoked on cleanup so the browser
  // doesn't hold the file in memory after the modal closes.
  useEffect(() => {
    if (!ndaModal || !ndaModal.nda || !ndaModal.nda.uploaded_at) {
      setNdaPreviewUrl(null);
      setNdaPreviewError(null);
      setNdaPreviewLoading(false);
      return;
    }
    let cancelled = false;
    let createdUrl = null;
    setNdaPreviewLoading(true);
    setNdaPreviewError(null);
    (async () => {
      try {
        const token = localStorage.getItem('isi_token');
        const res = await fetch(downloadNdaFile(ndaModal.startup.id), {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const rawBlob = await res.blob();
        if (cancelled) return;
        // Force application/pdf when the server returned octet-stream so
        // the browser's built-in PDF viewer kicks in inside the iframe.
        const mime = ndaModal.nda.mime_type || rawBlob.type || 'application/pdf';
        const blob = rawBlob.type === mime ? rawBlob : new Blob([rawBlob], { type: mime });
        createdUrl = URL.createObjectURL(blob);
        setNdaPreviewUrl(createdUrl);
      } catch (err) {
        if (!cancelled) setNdaPreviewError(err.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setNdaPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [ndaModal?.startup?.id, ndaModal?.nda?.uploaded_at]);

  const handleResolve = async (id) => {
    await resolveAlert(id);
    load();
  };

  const openActionModal = async (e, type, startup) => {
    e.stopPropagation();
    try {
      const investors = await getStartupInvestors(startup.id);
      setModal({ type, startup, investors });
    } catch (err) {
      setModal({ type, startup, investors: { watchlist: [], portfolio: [], total: 0 } });
    }
  };

  const confirmDeactivate = async () => {
    if (!modal) return;
    const { startup } = modal;
    const newStatus = startup.status === 'suspendu' ? 'actif' : 'suspendu';
    try {
      await validateDossier(startup.id, newStatus);
      setModal(null);
      load();
    } catch (err) { toast.error('Erreur : ' + err.message); }
  };

  const confirmDelete = async () => {
    if (!modal) return;
    const ok = await toast.confirm(`Dernière confirmation : supprimer définitivement "${modal.startup.name}" ?`);
    if (!ok) return;
    try {
      await deleteStartup(modal.startup.id);
      setModal(null);
      load();
    } catch (err) { toast.error('Erreur : ' + err.message); }
  };

  const openNdaModal = async (e, startup) => {
    if (e) e.stopPropagation();
    try {
      const data = await getAdminNda(startup.id);
      // Backend payload: { nda_record, audit_trail, startup }
      // Frontend expects: { nda, events, startup }
      setNdaModal({
        ...data,
        nda: data.nda_record || data.nda || null,
        events: data.audit_trail || data.events || [],
        startup,
      });
      setRejectReason('');
    } catch (err) { toast.error('Erreur: ' + err.message); }
  };

  const handleValidateNda = async () => {
    if (!ndaModal) return;
    setNdaAction(true);
    try {
      const result = await validateNda(ndaModal.startup.id);
      if (result.integrity) {
        toast.success('NDA validé avec succès. Intégrité SHA-256 vérifiée.');
      } else {
        toast.error("Attention : l'intégrité du fichier n'a pas pu être vérifiée.");
      }
      setNdaModal(null);
      load();
    } catch (err) { toast.error('Erreur: ' + err.message); }
    setNdaAction(false);
  };

  const handleRejectNda = async () => {
    if (!ndaModal) return;
    setNdaAction(true);
    try {
      await rejectNda(ndaModal.startup.id, rejectReason);
      setNdaModal(null);
      load();
    } catch (err) { toast.error('Erreur: ' + err.message); }
    setNdaAction(false);
  };

  const openAlerts = alerts.filter(a => a.status === 'open');

  // Client-side filtering
  const filtered = useMemo(() => {
    return dossiers.filter(d => {
      if (search && !d.name.toLowerCase().includes(search.toLowerCase()) &&
          !(d.categorie || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (secteurFilter && d.categorie !== secteurFilter) return false;
      if (riskFilter && d.risk_level !== riskFilter) return false;
      if (trophyFilter === 'oui' && !d.trophy_participant) return false;
      if (trophyFilter === 'non' && d.trophy_participant) return false;
      if (actionFilter === 'aucune' && d.dossier_action) return false;
      if (actionFilter && actionFilter !== 'aucune' && d.dossier_action !== actionFilter) return false;
      if (submittedFilter === 'soumis' && !d.candidature_submitted) return false;
      if (submittedFilter === 'non_soumis' && d.candidature_submitted) return false;
      if (qitMinFilter !== '' && (Number(d.qit_progress) || 0) < Number(qitMinFilter)) return false;
      return true;
    });
  }, [dossiers, search, secteurFilter, riskFilter, trophyFilter, actionFilter, submittedFilter, qitMinFilter]);

  const hasFilters = search || secteurFilter || riskFilter || trophyFilter || statusFilter || actionFilter || submittedFilter || qitMinFilter !== '';

  const handleExportCandidates = async () => {
    const ids = filtered.map(d => d.id);
    if (!ids.length) { toast.error('Aucune startup à exporter'); return; }
    setExportingList(true);
    try {
      const blob = await exportCandidatesCsv(ids);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'suivi_candidats.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Échec de l'export");
    } finally {
      setExportingList(false);
    }
  };

  // Stats "Comment vous nous avez connu" (referral_source) — calculées sur les
  // dossiers renseignés uniquement. Les valeurs legacy + le format "Autre:<texte>"
  // sont normalisés vers une option canonique de REFERRAL_SOURCES.
  const referralStats = useMemo(() => {
    const counts = {};
    let filled = 0;
    for (const d of dossiers) {
      const raw = (d.referral_source || '').trim();
      if (!raw) continue;
      filled++;
      let opt = parseReferralValue(migrateLegacyReferral(raw)).option || 'Autre';
      if (!REFERRAL_SOURCES.includes(opt)) opt = 'Autre';
      counts[opt] = (counts[opt] || 0) + 1;
    }
    const rows = REFERRAL_SOURCES
      .map(label => ({ label, count: counts[label] || 0 }))
      .filter(r => r.count > 0)
      .sort((a, b) => b.count - a.count);
    return { rows, filled, total: dossiers.length };
  }, [dossiers]);

  // Avancement QIT des candidats (Trophées) — tranches renvoyées par le backend.
  const qitBuckets = useMemo(() => {
    const q = completeness?.qit;
    return {
      total: completeness?.total_candidates || 0,
      rows: q ? [
        { label: '> 80% (quasi complet)', count: q.sup80, color: '#27AE60' },
        { label: 'de 50% à 80%', count: q.b50_80, color: '#4A71E5' },
        { label: 'de 10% à 49%', count: q.b10_49, color: '#F39C12' },
        { label: '> 0% et < 10%', count: q.gt0_lt10, color: '#E74C3C' },
        { label: '= 0% (non démarré)', count: q.eq0, color: '#95A5A6' },
      ] : [],
    };
  }, [completeness]);

  // Complétude des documents obligatoires des candidats (5 documents requis).
  const docBuckets = useMemo(() => {
    const d = completeness?.documents;
    return {
      total: completeness?.total_candidates || 0,
      rows: d ? [
        { label: `Complet (${completeness?.mandatory_docs_count || 5}/${completeness?.mandatory_docs_count || 5})`, count: d.complet, color: '#27AE60' },
        { label: 'Partiel', count: d.partiel, color: '#F39C12' },
        { label: 'Vide', count: d.vide, color: '#E74C3C' },
      ] : [],
    };
  }, [completeness]);

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <h1 style={{ marginBottom: 24 }}>Suivi des Startups</h1>

        {/* Stats */}
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          <div className="stat-card">
            <div className="stat-value">{dossiers.length}</div>
            <div className="stat-label">Dossiers total</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#27AE60' }}>{dossiers.filter(d => d.status === 'actif').length}</div>
            <div className="stat-label">Actifs</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#E74C3C' }}>{dossiers.filter(d => d.status === 'suspendu').length}</div>
            <div className="stat-label">Suspendus</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#E74C3C' }}>{openAlerts.length}</div>
            <div className="stat-label">Alertes ouvertes</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tab-bar">
          {['dossiers', 'nda', 'alertes', 'divers'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding: '12px 24px', border: 'none', background: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
                borderBottom: `3px solid ${tab === t ? 'var(--primary-light)' : 'transparent'}`,
                color: tab === t ? 'var(--primary)' : 'var(--text-light)', marginBottom: -2 }}>
              {t === 'dossiers' ? `Dossiers (${filtered.length})`
                : t === 'nda' ? `NDA à valider (${pendingNdas.length})`
                : t === 'alertes' ? `Alertes d'audit (${openAlerts.length})`
                : 'Divers'}
            </button>
          ))}
        </div>

        {tab === 'dossiers' && (
          <>
            {/* Search and filters */}
            <div className="card" style={{ marginBottom: 20, padding: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  className="form-input" placeholder="Rechercher par nom ou secteur..."
                  style={{ fontSize: 14 }} />
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="form-input" style={{ maxWidth: 180, fontSize: 13 }}>
                  <option value="">Tous les statuts</option>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select value={secteurFilter} onChange={e => setSecteurFilter(e.target.value)} className="form-input" style={{ maxWidth: 160, fontSize: 13 }}>
                  <option value="">Tous les secteurs</option>
                  {SECTEURS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={riskFilter} onChange={e => setRiskFilter(e.target.value)} className="form-input" style={{ maxWidth: 160, fontSize: 13 }}>
                  <option value="">Tous les risques</option>
                  <option value="vert">Vert (Résilient)</option>
                  <option value="orange">Orange (Vigilance)</option>
                  <option value="rouge">Rouge (Critique)</option>
                </select>
                <select value={trophyFilter} onChange={e => setTrophyFilter(e.target.value)} className="form-input" style={{ maxWidth: 160, fontSize: 13 }}>
                  <option value="">Trophées : tous</option>
                  <option value="oui">Participants</option>
                  <option value="non">Non participants</option>
                </select>
                <select value={actionFilter} onChange={e => setActionFilter(e.target.value)} className="form-input" style={{ maxWidth: 200, fontSize: 13 }}>
                  <option value="">Actions : toutes</option>
                  <option value="aucune">Aucune action</option>
                  {Object.entries(DOSSIER_ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select value={submittedFilter} onChange={e => setSubmittedFilter(e.target.value)} className="form-input" style={{ maxWidth: 180, fontSize: 13 }}>
                  <option value="">Candidature : toutes</option>
                  <option value="soumis">Soumises</option>
                  <option value="non_soumis">Non soumises</option>
                </select>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-light)' }}>
                  % QIT ≥
                  <input type="number" min="0" max="100" value={qitMinFilter}
                    onChange={e => setQitMinFilter(e.target.value)}
                    className="form-input" placeholder="0" style={{ width: 72, fontSize: 13 }} />
                </span>
                {hasFilters && (
                  <button onClick={() => { setSearch(''); setStatusFilter(''); setSecteurFilter(''); setRiskFilter(''); setTrophyFilter(''); setActionFilter(''); setSubmittedFilter(''); setQitMinFilter(''); }}
                    className="btn btn-outline" style={{ fontSize: 12, padding: '6px 14px' }}>
                    Réinitialiser
                  </button>
                )}
                <button onClick={handleExportCandidates} disabled={exportingList || filtered.length === 0}
                  className="btn btn-accent" style={{ fontSize: 12, padding: '6px 14px', marginLeft: 'auto',
                    opacity: (exportingList || filtered.length === 0) ? 0.6 : 1 }}>
                  {exportingList ? 'Export…' : `Exporter la liste (${filtered.length})`}
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gap: 8 }}>
              {filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-light)' }}>Aucun dossier ne correspond aux filtres.</div>
              ) : filtered.map(d => (
                <div key={d.id} className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', opacity: d.status === 'suspendu' ? 0.6 : 1,
                  borderLeft: d.status === 'suspendu' ? '4px solid #E74C3C' : undefined }}
                  onClick={() => navigate(`/analyst/dossiers/${d.id}`)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ fontSize: 15 }}>{d.name}</strong>
                      <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                        <span className="badge" style={{ background: `${STATUS_COLORS[d.status] || '#95A5A6'}20`, color: STATUS_COLORS[d.status] || '#95A5A6', fontSize: 11 }}>
                          {STATUS_LABELS[d.status] || d.status}
                        </span>
                        {d.categorie && <span className="badge badge-secteur" style={{ fontSize: 11 }}>{d.categorie}</span>}
                        {d.trophy_participant && <span className="badge" style={{ background: '#FFF8E1', color: '#F57F17', fontSize: 10 }}>Trophées</span>}
                        {d.trophy_participant && pendingNdas.some(n => n.startup_id === d.id) && (
                          <span className="badge" style={{ background: '#FFF3E0', color: '#E65100', fontSize: 10 }}>NDA en attente</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, textAlign: 'center', flexShrink: 0 }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-light)' }}>Action</div>
                      {(() => {
                        const actionKey = d.dossier_action || null;
                        const actionInfo = actionKey && DOSSIER_ACTION_LABELS[actionKey];
                        return actionInfo ? (
                          <span className="badge" style={{ fontSize: 10, background: actionInfo.bg, color: actionInfo.color, fontWeight: 600, padding: '3px 8px' }}>
                            {actionInfo.label}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: '#BDBDBD', fontStyle: 'italic' }}>Aucune</span>
                        );
                      })()}
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-light)' }}>Candidature</div>
                      {d.candidature_submitted ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', marginTop: 2 }}>
                          <span className="badge" style={{ fontSize: 10, background: '#E8F5E9', color: '#2E7D32', fontWeight: 600, padding: '3px 8px' }}>
                            Soumis
                          </span>
                          {isAdmin && (
                            d.score_published ? (
                              <span title="Score ISI communiqué à la startup — déverrouillage impossible"
                                style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid #E0E0E0', background: '#F5F5F5',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, opacity: 0.55, cursor: 'not-allowed' }}>
                                🔒
                              </span>
                            ) : (
                              <button onClick={(e) => handleUnlockCandidature(e, d)} disabled={unlockingId === d.id}
                                title="Déverrouiller la candidature — réouvre l'édition du dossier pour la startup"
                                style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid #FFE082', background: '#FFFDF5',
                                  cursor: unlockingId === d.id ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                                  opacity: unlockingId === d.id ? 0.5 : 1 }}>
                                🔓
                              </button>
                            )
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: '#BDBDBD', fontStyle: 'italic' }}>Non soumis</span>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-light)' }}>QIT</div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: d.qit_progress >= 100 ? '#27AE60' : '#F39C12' }}>{Math.max(0, Math.min(100, d.qit_progress || 0))}%</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-light)' }}>Docs</div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{d.doc_count || 0}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-light)' }}>Note ISI</div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: parseFloat(d.note_sectorielle) > 7 ? '#27AE60' : '#F39C12' }}>
                        {d.note_sectorielle ? parseFloat(d.note_sectorielle).toFixed(1) : '-'}
                      </div>
                    </div>
                    {/* NDA button */}
                    {isAdmin && d.trophy_participant && (
                      <button onClick={(e) => openNdaModal(e, d)}
                        title="Consulter le NDA"
                        style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #D0E3F5', background: '#F5F9FF',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                        📄
                      </button>
                    )}
                    {/* Admin actions */}
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 6, marginLeft: 8 }}>
                        <button onClick={(e) => openActionModal(e, 'deactivate', d)}
                          title={d.status === 'suspendu' ? 'Réactiver' : 'Désactiver'}
                          style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)', background: 'white',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                          {d.status === 'suspendu' ? '🔓' : '🔒'}
                        </button>
                        <button onClick={(e) => openActionModal(e, 'delete', d)}
                          title="Supprimer définitivement"
                          style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #FFCDD2', background: '#FFF5F5',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'alertes' && (
          <div style={{ display: 'grid', gap: 8 }}>
            {alerts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-light)' }}>Aucune alerte d'audit</div>
            ) : alerts.map(a => (
              <div key={a.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderLeft: `4px solid ${SEV_COLORS[a.severity] || '#95A5A6'}` }}>
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span className="badge" style={{ background: `${SEV_COLORS[a.severity]}20`, color: SEV_COLORS[a.severity], fontSize: 11 }}>
                      {a.severity}
                    </span>
                    <strong>{a.startup_name}</strong>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-light)' }}>
                    {a.description || `${a.field_name}: déclaré "${a.declared_value}" vs extrait "${a.extracted_value}" (écart ${a.ecart}%)`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {a.status === 'open' ? (
                    <button onClick={() => handleResolve(a.id)} className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }}>
                      Résoudre
                    </button>
                  ) : (
                    <span className="badge badge-vert" style={{ fontSize: 11 }}>Résolu</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'nda' && (
          <div style={{ display: 'grid', gap: 8 }}>
            {pendingNdas.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-light)' }}>Aucun NDA en attente de validation</div>
            ) : pendingNdas.map(n => (
              <div key={n.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderLeft: '4px solid #2E75B6' }}>
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <strong>{n.current_startup_name || n.startup_name}</strong>
                    {n.categorie && <span className="badge badge-secteur" style={{ fontSize: 10 }}>{n.categorie}</span>}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-light)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <span>📄 {n.original_filename}</span>
                    <span>📏 {(n.file_size / 1024).toFixed(1)} Ko</span>
                    <span>📅 Déposé le {new Date(n.uploaded_at).toLocaleDateString('fr-FR')} à {new Date(n.uploaded_at).toLocaleTimeString('fr-FR')}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => openNdaModal(null, { id: n.startup_id, name: n.current_startup_name || n.startup_name })}
                    className="btn btn-primary" style={{ fontSize: 12, padding: '8px 16px' }}>
                    Consulter / Valider
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'divers' && (
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="card" style={{ padding: 24 }}>
              <h3 style={{ marginTop: 0, marginBottom: 4, fontSize: 16 }}>Comment vous nous avez connu</h3>
              <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 20 }}>
                {referralStats.filled > 0
                  ? `${referralStats.filled} dossier${referralStats.filled > 1 ? 's' : ''} renseigné${referralStats.filled > 1 ? 's' : ''} sur ${referralStats.total} (${referralStats.total ? Math.round((referralStats.filled / referralStats.total) * 100) : 0}%)`
                  : 'Champ non renseigné'}
              </div>
              {referralStats.filled === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>
                  Aucun dossier n'a renseigné ce champ.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  {referralStats.rows.map(r => {
                    const pct = (r.count / referralStats.filled) * 100;
                    return (
                      <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 180, fontSize: 13, fontWeight: 500, flexShrink: 0 }}>{r.label}</div>
                        <div style={{ flex: 1, background: '#EEF1FB', borderRadius: 6, height: 22, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: 'var(--primary)', borderRadius: 6, minWidth: pct > 0 ? 2 : 0 }} />
                        </div>
                        <div style={{ width: 96, textAlign: 'right', fontSize: 13, flexShrink: 0 }}>
                          <strong>{pct.toFixed(1)}%</strong>
                          <span style={{ color: 'var(--text-light)' }}> ({r.count})</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bloc : avancement de la complétude du QIT (candidats Trophées) */}
            <div className="card" style={{ padding: 24 }}>
              <h3 style={{ marginTop: 0, marginBottom: 4, fontSize: 16 }}>Avancement de la complétude du QIT</h3>
              <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 20 }}>
                Répartition des <strong>{qitBuckets.total}</strong> startups candidates par tranche d'avancement du questionnaire.
              </div>
              {qitBuckets.total === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>Aucune startup candidate.</div>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  {qitBuckets.rows.map(b => {
                    const pct = qitBuckets.total ? (b.count / qitBuckets.total) * 100 : 0;
                    return (
                      <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 180, fontSize: 13, fontWeight: 500, flexShrink: 0 }}>{b.label}</div>
                        <div style={{ flex: 1, background: '#EEF1FB', borderRadius: 6, height: 22, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: b.color, borderRadius: 6, minWidth: pct > 0 ? 2 : 0 }} />
                        </div>
                        <div style={{ width: 96, textAlign: 'right', fontSize: 13, flexShrink: 0 }}>
                          <strong>{pct.toFixed(1)}%</strong>
                          <span style={{ color: 'var(--text-light)' }}> ({b.count})</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bloc : complétude des documents obligatoires (candidats Trophées) */}
            <div className="card" style={{ padding: 24 }}>
              <h3 style={{ marginTop: 0, marginBottom: 4, fontSize: 16 }}>Complétude des documents obligatoires</h3>
              <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 20, lineHeight: 1.5 }}>
                Sur les <strong>{docBuckets.total}</strong> startups candidates. {docBuckets.total > 0 && '5 documents requis : Business Plan, Extrait Kbis, Statuts à jour, CVs des dirigeants, Comptes annuels.'}
              </div>
              {docBuckets.total === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>Aucune startup candidate.</div>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  {docBuckets.rows.map(b => {
                    const pct = docBuckets.total ? (b.count / docBuckets.total) * 100 : 0;
                    return (
                      <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 180, fontSize: 13, fontWeight: 500, flexShrink: 0 }}>{b.label}</div>
                        <div style={{ flex: 1, background: '#EEF1FB', borderRadius: 6, height: 22, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: b.color, borderRadius: 6, minWidth: pct > 0 ? 2 : 0 }} />
                        </div>
                        <div style={{ width: 96, textAlign: 'right', fontSize: 13, flexShrink: 0 }}>
                          <strong>{pct.toFixed(1)}%</strong>
                          <span style={{ color: 'var(--text-light)' }}> ({b.count})</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal NDA consultation + validation */}
      {ndaModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setNdaModal(null)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 1100, width: '95%', maxHeight: '92vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ marginBottom: 16, color: '#2E75B6', display: 'flex', alignItems: 'center', gap: 8 }}>
              📄 NDA — {ndaModal.startup.name}
            </h2>

            {!ndaModal.nda ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>
                Aucun NDA n'a été déposé par cette startup.
              </div>
            ) : (
              <>
                {/* NDA Info */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                  <div style={{ padding: 12, background: '#F5F9FF', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-light)', marginBottom: 4 }}>Statut</div>
                    <span className="badge" style={{
                      background: ndaModal.nda.status === 'validated' ? '#E8F5E9' : ndaModal.nda.status === 'rejected' ? '#FFEBEE' : ndaModal.nda.status === 'uploaded' ? '#FFF3E0' : '#E3F2FD',
                      color: ndaModal.nda.status === 'validated' ? '#2E7D32' : ndaModal.nda.status === 'rejected' ? '#C62828' : ndaModal.nda.status === 'uploaded' ? '#E65100' : '#1565C0',
                      fontSize: 12
                    }}>
                      {ndaModal.nda.status === 'validated' ? 'Validé' : ndaModal.nda.status === 'rejected' ? 'Rejeté' : ndaModal.nda.status === 'uploaded' ? 'En attente' : 'Téléchargé'}
                    </span>
                  </div>
                  <div style={{ padding: 12, background: '#F5F9FF', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-light)', marginBottom: 4 }}>Fichier</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{ndaModal.nda.original_filename || '-'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-light)' }}>
                      {ndaModal.nda.file_size ? `${(ndaModal.nda.file_size / 1024).toFixed(1)} Ko` : ''}
                      {ndaModal.nda.mime_type ? ` — ${ndaModal.nda.mime_type}` : ''}
                    </div>
                  </div>
                  <div style={{ padding: 12, background: '#F5F9FF', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-light)', marginBottom: 4 }}>SHA-256 (eIDAS)</div>
                    <div style={{ fontSize: 10, fontFamily: 'monospace', wordBreak: 'break-all', color: '#333' }}>
                      {ndaModal.nda.sha256_hash || '-'}
                    </div>
                  </div>
                  <div style={{ padding: 12, background: '#F5F9FF', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-light)', marginBottom: 4 }}>Startup</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{ndaModal.nda.startup_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-light)' }}>ID: {ndaModal.nda.startup_id}</div>
                  </div>
                </div>

                {/* Inline document viewer (PDF or image) */}
                {ndaModal.nda.uploaded_at && (
                  <div style={{ marginBottom: 16 }}>
                    <h3 style={{ fontSize: 15, color: '#2E75B6', marginBottom: 10 }}>
                      Aperçu du document
                    </h3>
                    <div style={{
                      width: '100%', height: '60vh', minHeight: 420,
                      border: '1px solid #E0E0E0', borderRadius: 8,
                      background: '#F8FAFC', overflow: 'hidden',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {ndaPreviewLoading && (
                        <div style={{ color: 'var(--text-light)', fontSize: 13 }}>Chargement du document...</div>
                      )}
                      {!ndaPreviewLoading && ndaPreviewError && (
                        <div style={{ color: '#C62828', fontSize: 13, textAlign: 'center', padding: 16 }}>
                          Impossible d'afficher le document ({ndaPreviewError}).<br />
                          Utilisez le bouton « Télécharger » ci-dessous.
                        </div>
                      )}
                      {!ndaPreviewLoading && !ndaPreviewError && ndaPreviewUrl && (
                        (ndaModal.nda.mime_type || '').startsWith('image/') ? (
                          <img
                            src={ndaPreviewUrl}
                            alt={ndaModal.nda.original_filename || 'NDA'}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        ) : (
                          <iframe
                            src={ndaPreviewUrl}
                            title={`Aperçu NDA ${ndaModal.startup.name}`}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                          />
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* Download button (kept available even when the inline preview works) */}
                {ndaModal.nda.uploaded_at && (
                  <div style={{ marginBottom: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={async () => {
                      try {
                        const token = localStorage.getItem('isi_token');
                        const res = await fetch(downloadNdaFile(ndaModal.startup.id), { headers: { 'Authorization': `Bearer ${token}` } });
                        if (!res.ok) throw new Error('Erreur téléchargement');
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = ndaModal.nda.original_filename || 'NDA.pdf';
                        a.click(); URL.revokeObjectURL(url);
                      } catch (err) { toast.error('Erreur: ' + err.message); }
                    }}
                      className="btn btn-outline" style={{ fontSize: 13, padding: '8px 20px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      📥 Télécharger le NDA signé
                    </button>
                    {ndaPreviewUrl && (
                      <button onClick={() => window.open(ndaPreviewUrl, '_blank', 'noopener,noreferrer')}
                        className="btn btn-outline" style={{ fontSize: 13, padding: '8px 20px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        ↗ Ouvrir dans un nouvel onglet
                      </button>
                    )}
                  </div>
                )}

                {/* Audit trail */}
                <h3 style={{ fontSize: 15, color: '#2E75B6', marginBottom: 12, borderTop: '1px solid #E0E0E0', paddingTop: 16 }}>
                  Traçabilité complète
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20, maxHeight: 240, overflowY: 'auto' }}>
                  {!Array.isArray(ndaModal.events) || ndaModal.events.length === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--text-light)' }}>Aucun événement enregistré.</div>
                  ) : ndaModal.events.map(evt => (
                    <div key={evt.id} style={{ padding: '8px 12px', background: '#FAFAFA', borderRadius: 6, borderLeft: '3px solid #2E75B6' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#2E75B6' }}>{evt.event_type.replace(/_/g, ' ')}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-light)' }}>
                          {new Date(evt.created_at).toLocaleDateString('fr-FR')} à {new Date(evt.created_at).toLocaleTimeString('fr-FR')}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text)', marginTop: 4 }}>{evt.event_description}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 2 }}>
                        Par : {evt.user_email} ({evt.user_role})
                      </div>
                    </div>
                  ))}
                </div>

                {/* Actions admin */}
                {ndaModal.nda.status === 'uploaded' && isAdmin && (
                  <div style={{ borderTop: '1px solid #E0E0E0', paddingTop: 16 }}>
                    <h3 style={{ fontSize: 15, color: '#2E75B6', marginBottom: 12 }}>Actions</h3>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                      <button onClick={handleValidateNda} disabled={ndaAction}
                        className="btn" style={{ background: '#27AE60', color: 'white', fontSize: 14, padding: '10px 24px' }}>
                        {ndaAction ? '...' : '✓ Valider le NDA'}
                      </button>
                      <button onClick={handleRejectNda} disabled={ndaAction || !rejectReason.trim()}
                        className="btn" style={{ background: '#E74C3C', color: 'white', fontSize: 14, padding: '10px 24px',
                          opacity: rejectReason.trim() ? 1 : 0.5 }}>
                        {ndaAction ? '...' : '✗ Rejeter'}
                      </button>
                    </div>
                    <input type="text" value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                      className="form-input" placeholder="Motif du rejet (obligatoire pour rejeter)..."
                      style={{ fontSize: 13 }} />
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setNdaModal(null)} className="btn btn-outline" style={{ fontSize: 14 }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation with investor impact */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setModal(null)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 640, width: '90%', maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ marginBottom: 16, color: modal.type === 'delete' ? '#E74C3C' : '#F39C12' }}>
              {modal.type === 'delete' ? '🗑️ Suppression définitive' : (modal.startup.status === 'suspendu' ? '🔓 Réactivation' : '🔒 Désactivation')}
            </h2>
            <p style={{ fontSize: 15, marginBottom: 16 }}>
              {modal.type === 'delete'
                ? <>Vous êtes sur le point de <strong>supprimer définitivement</strong> la startup <strong>"{modal.startup.name}"</strong>. Cette action est irréversible.</>
                : modal.startup.status === 'suspendu'
                  ? <>Vous allez <strong>réactiver</strong> la startup <strong>"{modal.startup.name}"</strong>. Elle sera de nouveau visible par les investisseurs et le jury.</>
                  : <>Vous allez <strong>désactiver</strong> la startup <strong>"{modal.startup.name}"</strong>. Elle ne sera plus visible par les investisseurs et le jury.</>
              }
            </p>

            {/* Investor impact table */}
            {modal.investors.total > 0 ? (
              <div style={{ marginBottom: 20, border: '2px solid #FFF3E0', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ background: '#FFF3E0', padding: '10px 16px', fontWeight: 700, color: '#E65100', fontSize: 14 }}>
                  ⚠️ {modal.investors.total} investisseur{modal.investors.total > 1 ? 's' : ''} impacté{modal.investors.total > 1 ? 's' : ''}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #E0E0E0' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Investisseur</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Email</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Société</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center' }}>Relation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modal.investors.watchlist.map((inv, i) => (
                      <tr key={`w-${i}`} style={{ borderBottom: '1px solid #F0F0F0' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{inv.first_name} {inv.last_name}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-light)' }}>{inv.email}</td>
                        <td style={{ padding: '8px 12px' }}>{inv.company || '-'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <span className="badge" style={{ background: '#E3F2FD', color: '#1565C0', fontSize: 11 }}>⭐ Favori</span>
                        </td>
                      </tr>
                    ))}
                    {modal.investors.portfolio.map((inv, i) => (
                      <tr key={`p-${i}`} style={{ borderBottom: '1px solid #F0F0F0' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{inv.first_name} {inv.last_name}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-light)' }}>{inv.email}</td>
                        <td style={{ padding: '8px 12px' }}>{inv.company || '-'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <span className="badge" style={{ background: '#E8F5E9', color: '#2E7D32', fontSize: 11 }}>
                            💼 Portefeuille{inv.portfolio_status ? ` (${inv.portfolio_status})` : ''}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '12px 16px', background: '#E8F5E9', borderRadius: 8, marginBottom: 20, fontSize: 13, color: '#2E7D32' }}>
                Aucun investisseur n'a cette startup dans ses favoris ou son portefeuille.
              </div>
            )}

            {modal.type === 'delete' && (
              <div style={{ padding: '12px 16px', background: '#FFEBEE', borderRadius: 8, marginBottom: 20, fontSize: 13, color: '#C62828' }}>
                <strong>Données supprimées :</strong> profil, scores ISI, réponses QIT, documents, participations Trophées, demandes de contact, favoris et portefeuilles des investisseurs.
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)} className="btn btn-outline" style={{ fontSize: 14 }}>Annuler</button>
              {modal.type === 'delete' ? (
                <button onClick={confirmDelete} className="btn" style={{ background: '#E74C3C', color: 'white', fontSize: 14 }}>
                  Supprimer définitivement
                </button>
              ) : (
                <button onClick={confirmDeactivate} className="btn" style={{ background: modal.startup.status === 'suspendu' ? '#27AE60' : '#F39C12', color: 'white', fontSize: 14 }}>
                  {modal.startup.status === 'suspendu' ? 'Réactiver' : 'Désactiver'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
